import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  ClipboardText, Check, X, Prohibit, Trash, ArrowSquareOut,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  adminListOrderDrafts, approveOrderDraft, rejectOrderDraft,
  cancelOrderDraft, deleteOrderDraft, getSuppliers, getEmployees,
} from '../../services/api';

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-700',
};

const OrderDraftsPage = () => {
  const [tab, setTab] = useState('pending');
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [agents, setAgents] = useState([]);

  const [approveDraft, setApproveDraft] = useState(null);
  const [approveForm, setApproveForm] = useState({
    selling_price: '', purchase_price: '', deposit_amount: '', supplier_id: '', assigned_agent_id: '', admin_notes: '',
  });
  const [approving, setApproving] = useState(false);

  const [rejectDraft, setRejectDraft] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectMode, setRejectMode] = useState('reject'); // 'reject' | 'cancel'

  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminListOrderDrafts(tab === 'all' ? null : tab);
      setDrafts(r.data || []);
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      try { const r = await getSuppliers({ is_active: true }); setSuppliers(r.data || []); } catch { /* ignore */ }
      try { const r = await getEmployees(); setAgents((r.data || []).filter(e => ['sales_agent', 'logistics_manager', 'admin'].includes(e.role))); } catch { /* ignore */ }
    })();
  }, []);

  const openApprove = (d) => {
    setApproveDraft(d);
    setApproveForm({
      selling_price: '', purchase_price: '', deposit_amount: '',
      supplier_id: '', assigned_agent_id: '', admin_notes: '',
    });
  };
  const submitApprove = async () => {
    const price = Number(approveForm.selling_price);
    if (!price || price <= 0) { toast.error('Selling price is required'); return; }
    setApproving(true);
    try {
      const payload = {
        selling_price: price,
        purchase_price: approveForm.purchase_price ? Number(approveForm.purchase_price) : null,
        deposit_amount: approveForm.deposit_amount ? Number(approveForm.deposit_amount) : 0,
        supplier_id: approveForm.supplier_id || null,
        assigned_agent_id: approveForm.assigned_agent_id || null,
        admin_notes: approveForm.admin_notes || '',
      };
      const r = await approveOrderDraft(approveDraft.id, payload);
      toast.success(`Order ${r.data?.order?.order_number || ''} created — dealer notified by email.`);
      setApproveDraft(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    } finally { setApproving(false); }
  };

  const openReject = (d, mode = 'reject') => {
    setRejectMode(mode);
    setRejectDraft(d);
    setRejectReason('');
  };
  const submitReject = async () => {
    try {
      if (rejectMode === 'reject') {
        await rejectOrderDraft(rejectDraft.id, rejectReason);
        toast.success('Request rejected — dealer notified by email');
      } else {
        await cancelOrderDraft(rejectDraft.id, rejectReason);
        toast.success('Request cancelled — dealer notified by email');
      }
      setRejectDraft(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const removeDraft = async (d) => {
    if (!window.confirm(`Remove draft from ${d.client_name}? This is permanent and the dealer will be emailed about it.`)) return;
    try {
      await deleteOrderDraft(d.id);
      toast.success('Request removed — dealer notified');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4 p-6" data-testid="drafts-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <ClipboardText className="w-7 h-7" weight="duotone" /> Order Requests from Dealers
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Review and approve order requests submitted by dealers from their portal. When you approve, set the agreed price —
          a confirmed order is auto-created and the dealer is notified by email.
        </p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="cancelled" data-testid="tab-cancelled">Cancelled</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-3">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : drafts.length === 0 ? (
            <div className="border border-dashed border-border p-10 text-center text-muted-foreground text-sm">No requests in this category.</div>
          ) : (
            <div className="border border-border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/40">
                  <tr>
                    <th className="text-left p-3">Submitted</th>
                    <th className="text-left p-3">Dealer</th>
                    <th className="text-left p-3">Car</th>
                    <th className="text-center p-3">Qty</th>
                    <th className="text-left p-3">End client</th>
                    <th className="text-left p-3">Notes</th>
                    <th className="text-center p-3">Status</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {drafts.map((d) => (
                    <tr key={d.id} className="border-t border-border" data-testid={`draft-row-${d.id}`}>
                      <td className="p-3 text-xs">
                        {fmt(d.created_at)}
                        {d.submitted_by_name && d.submitted_by_name !== d.client_name && (
                          <div className="text-[10px] text-muted-foreground">by {d.submitted_by_name}</div>
                        )}
                      </td>
                      <td className="p-3 font-medium">{d.client_name}</td>
                      <td className="p-3">{d.brand} {d.model} <span className="text-muted-foreground">· {d.color || '—'}</span></td>
                      <td className="p-3 text-center">{d.quantity}</td>
                      <td className="p-3 text-xs">
                        {d.end_client_name || '—'}
                        {d.end_client_snapshot?.nin_number && <div className="text-[10px] text-muted-foreground">NIN {d.end_client_snapshot.nin_number}</div>}
                      </td>
                      <td className="p-3 text-xs max-w-[250px] truncate" title={d.notes}>{d.notes || '—'}</td>
                      <td className="p-3 text-center">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGE[d.status] || ''}`}>{d.status}</span>
                        {d.status === 'rejected' && d.rejection_reason && (
                          <div className="text-[10px] text-red-700 mt-1" title={d.rejection_reason}>{d.rejection_reason.slice(0, 30)}{d.rejection_reason.length > 30 ? '…' : ''}</div>
                        )}
                        {d.status === 'cancelled' && d.cancellation_reason && (
                          <div className="text-[10px] text-gray-700 mt-1">{d.cancellation_reason.slice(0, 30)}</div>
                        )}
                      </td>
                      <td className="p-3 text-right whitespace-nowrap">
                        {d.status === 'pending' && (
                          <>
                            <Button size="sm" variant="ghost" className="text-emerald-700" onClick={() => openApprove(d)} title="Approve & create order" data-testid={`approve-draft-${d.id}`}>
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => openReject(d, 'reject')} title="Reject" data-testid={`reject-draft-${d.id}`}>
                              <X className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-gray-600" onClick={() => openReject(d, 'cancel')} title="Cancel">
                              <Prohibit className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {d.status === 'approved' && d.linked_order_id && (
                          <Button size="sm" variant="ghost" onClick={() => navigate(`/orders/${d.linked_order_id}`)} title="Open order">
                            <ArrowSquareOut className="w-4 h-4" />
                            <span className="ml-1 text-xs">{d.linked_order_number || ''}</span>
                          </Button>
                        )}
                        {d.status !== 'approved' && (
                          <Button size="sm" variant="ghost" className="text-red-700" onClick={() => removeDraft(d)} title="Remove">
                            <Trash className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Approve dialog */}
      <Dialog open={!!approveDraft} onOpenChange={(o) => !o && setApproveDraft(null)}>
        <DialogContent className="max-w-xl" data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve & create order</DialogTitle>
          </DialogHeader>
          {approveDraft && (
            <div className="space-y-4 text-sm">
              <div className="bg-muted/30 border border-border rounded p-3 space-y-1">
                <div><strong>Dealer:</strong> {approveDraft.client_name}</div>
                <div><strong>Vehicle:</strong> {approveDraft.quantity}× {approveDraft.brand} {approveDraft.model} · {approveDraft.color}</div>
                <div><strong>End client:</strong> {approveDraft.end_client_name || '—'}</div>
                {approveDraft.notes && <div><strong>Notes:</strong> {approveDraft.notes}</div>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Selling price (DA) *</Label>
                  <Input type="number" value={approveForm.selling_price}
                    onChange={(e) => setApproveForm({ ...approveForm, selling_price: e.target.value })}
                    placeholder="e.g. 2750000" data-testid="approve-selling-price" />
                </div>
                <div>
                  <Label>Purchase price (DA, internal)</Label>
                  <Input type="number" value={approveForm.purchase_price}
                    onChange={(e) => setApproveForm({ ...approveForm, purchase_price: e.target.value })} />
                </div>
                <div>
                  <Label>Deposit amount (DA)</Label>
                  <Input type="number" value={approveForm.deposit_amount}
                    onChange={(e) => setApproveForm({ ...approveForm, deposit_amount: e.target.value })}
                    placeholder="0" />
                </div>
                <div>
                  <Label>Assigned agent</Label>
                  <Select value={approveForm.assigned_agent_id || 'none'} onValueChange={(v) => setApproveForm({ ...approveForm, assigned_agent_id: v === 'none' ? '' : v })}>
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {agents.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name} ({a.role})</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Supplier</Label>
                  <Select value={approveForm.supplier_id || 'none'} onValueChange={(v) => setApproveForm({ ...approveForm, supplier_id: v === 'none' ? '' : v })}>
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Internal notes</Label>
                  <Textarea rows={2} value={approveForm.admin_notes}
                    onChange={(e) => setApproveForm({ ...approveForm, admin_notes: e.target.value })} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                A confirmed order will be created with <strong>source = Dealer Portal</strong> and the dealer will be notified by email.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDraft(null)}>Cancel</Button>
            <Button onClick={submitApprove} disabled={approving} data-testid="approve-confirm-btn">
              {approving ? 'Creating…' : 'Approve & create order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject / Cancel dialog */}
      <Dialog open={!!rejectDraft} onOpenChange={(o) => !o && setRejectDraft(null)}>
        <DialogContent className="max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>{rejectMode === 'cancel' ? 'Cancel request' : 'Reject request'}</DialogTitle>
          </DialogHeader>
          {rejectDraft && (
            <div className="space-y-3 text-sm">
              <div className="bg-muted/30 border rounded p-2">
                {rejectDraft.client_name} — {rejectDraft.brand} {rejectDraft.model} {rejectDraft.color}
              </div>
              <div>
                <Label>Reason (sent to dealer by email)</Label>
                <Textarea
                  rows={4} value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder={rejectMode === 'cancel' ? 'e.g. Dealer asked to cancel' : 'e.g. Model not available'}
                  data-testid="reject-reason"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDraft(null)}>Back</Button>
            <Button variant="destructive" onClick={submitReject} data-testid="reject-confirm-btn">
              {rejectMode === 'cancel' ? 'Cancel request' : 'Reject request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderDraftsPage;
